import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Badge,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'js/ui/legacy';
import Spinner from '../Spinner';

const value = (source, ...keys) => {
  for (const key of keys) {
    if (source?.[key] != null) return source[key];
  }
  return '';
};

const nullableValue = (valueToNormalize) =>
  valueToNormalize === '' || valueToNormalize == null ? null : valueToNormalize;

export const createFormulaSupportSnapshot = (order) => {
  const item = order?.item || {};
  const can = order?.can;
  const base = order?.base;
  const product = order?.product;
  const colorants = item.cnts || order?.formula?.cnts;

  if (
    !can ||
    !base ||
    !product ||
    !Array.isArray(colorants) ||
    !colorants.length
  ) {
    return null;
  }

  return {
    baseid:
      Number.isInteger(base.baseid) && base.baseid > 0 ? base.baseid : null,
    basecode: value(base, 'basecode'),
    basevolume: can.basevolume,
    cnts: colorants.map(({ cntcode, volume }) => ({
      cntcode,
      volume,
    })),
  };
};

export const FormulaSupportModal = ({
  type,
  show,
  order,
  requestProduct,
  requestColor,
  requestColorCode,
  currentUser,
  supportState,
  onCancel,
  onSubmit,
  onLookup,
  onOpenCase,
  canOpenCase,
  t,
}) => {
  const [form, setForm] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const orderid = order?.orderid || order?.item?.orderid;
  const validOrderId = Number.isInteger(orderid) && orderid > 0;
  const lookupFailed =
    supportState?.lookupOrderId === orderid && supportState?.lookupError;
  const lookupReady =
    supportState?.lookupOrderId === orderid &&
    !supportState?.lookupLoading &&
    !lookupFailed;
  const existing =
    type === 'feedback' && lookupReady ? supportState?.orderFeedbackCase : null;
  const originalMessage = existing?.messages.find(
    (message) => message.sender === 'POS'
  );
  const productInvalid =
    !Number.isInteger(form.productid) || form.productid <= 0;
  const colourInvalid = !String(form.colourcode || '').trim();
  const formulaInvalid =
    type === 'feedback' &&
    (!form.formula ||
      !Number.isInteger(form.formula.baseid) ||
      form.formula.baseid <= 0);

  useEffect(() => {
    if (!show) {
      setSubmitted(false);
      setFieldErrors({});
    }
  }, [show]);

  useEffect(() => {
    if (show && type === 'feedback' && validOrderId) onLookup(orderid);
  }, [show, type, orderid, validOrderId, onLookup]);

  useEffect(() => {
    if (!show) return;
    const color = requestColor || order?.color || {};
    const product = requestProduct || order?.product || {};
    const can = order?.can || {};
    const item = order?.item || {};
    setForm({
      // A completed/history order keeps the authoritative display values on
      // `item`; the search objects are only partially populated.
      productid: value(product, 'productid') || null,
      productname:
        (requestProduct
          ? value(product, 'productzname', 'productname', 'name')
          : value(item, 'productname', 'productzname')) ||
        value(product, 'productzname', 'productname', 'name'),
      cansizecode:
        value(item, 'cansizecode') ||
        value(can, 'cansizecode', 'cancode', 'code'),
      colourcode:
        requestColorCode ||
        (requestColor
          ? value(color, 'colourcode', 'colorcode')
          : value(item, 'colourcode')) ||
        value(color, 'colourcode', 'colorcode'),
      colourname:
        (requestColor
          ? value(color, 'colourname', 'colorname')
          : value(item, 'colourname')) ||
        value(color, 'colourname', 'colorname'),
      colourcard:
        (requestColor
          ? value(color, 'colourcard', 'cardname')
          : value(item, 'cardname', 'colourcard')) ||
        value(color, 'colourcard', 'cardname'),
      rgb: nullableValue(
        requestColor
          ? value(color, 'rgb')
          : value(item, 'rgb') !== ''
            ? value(item, 'rgb')
            : value(color, 'rgb')
      ),
      orderid: order?.orderid || item?.orderid || null,
      username: currentUser?.username || 'POS',
      priority: 'N',
      notes: '',
      ...(type === 'feedback'
        ? {
            formula: createFormulaSupportSnapshot({
              ...order,
              ...(requestProduct ? { product: requestProduct } : {}),
              ...(requestColor ? { color: requestColor } : {}),
            }),
          }
        : {}),
    });
  }, [
    show,
    order,
    requestProduct,
    requestColor,
    requestColorCode,
    currentUser,
    type,
  ]);

  useEffect(() => {
    if (!show) return;

    const errors = {};
    if (productInvalid) {
      errors.productname = t(
        'msg.existingProductRequired',
        'Select an existing product'
      );
    }
    if (colourInvalid) {
      errors.colourcode = t('msg.colorCodeRequired', 'Color code is required');
    }
    if (formulaInvalid) {
      errors.formula = t(
        'msg.formulaBaseRequired',
        'Formula feedback requires an existing base'
      );
    }
    setFieldErrors(errors);
  }, [show, productInvalid, colourInvalid, formulaInvalid, t]);

  const update = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const request = type === 'request';
  return (
    <Modal
      isOpen={show}
      centered
      size="lg"
      toggle={submitted ? undefined : onCancel}
    >
      <ModalHeader toggle={submitted ? undefined : onCancel}>
        {request
          ? t('lbl.newFormulaRequest', 'New formula request')
          : t('lbl.formulaFeedback', 'Formula feedback')}
      </ModalHeader>

      <ModalBody
        className={
          existing
            ? 'formula-support-modal formula-feedback-existing'
            : 'formula-support-modal'
        }
      >
        {existing ? (
          <div data-testid="existing-formula-feedback">
            <Alert color="info" className="py-2 mb-3">
              {t(
                'msg.formulaFeedbackAlreadySent',
                'Feedback has already been sent for this order.'
              )}
            </Alert>
            <div className="d-flex align-items-center border-bottom pb-2 mb-3">
              <Badge color="primary" className="mr-2">
                {t(`lbl.supportStatus.${existing.status}`, existing.status)}
              </Badge>
              <strong>
                {t('lbl.requestId', 'Request ID')} #{existing.supportcaseid}
              </strong>
            </div>
            <div className="row">
              <div className="col-6 border-right">
                <div className="mb-2">
                  <strong>{t('lbl.product', 'Product')}</strong>
                  <div className="text-break">
                    {originalMessage?.productname || '—'}
                  </div>
                </div>
                <div className="mb-2">
                  <strong>{t('lbl.canSize', 'Can size')}</strong>
                  <div>{originalMessage?.cansizecode || '—'}</div>
                </div>
              </div>
              <div className="col-6">
                <div className="mb-2">
                  <strong>{t('lbl.colorCode', 'Color code')}</strong>
                  <div className="text-break">
                    {originalMessage?.colourcode || '—'}
                  </div>
                </div>
                <div className="mb-2">
                  <strong>{t('lbl.colorName', 'Color name')}</strong>
                  <div className="text-break">
                    {originalMessage?.colourname || '—'}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-light border rounded p-2 mb-2">
              <strong>{t('lbl.notes', 'Notes')}</strong>
              <div className="formula-feedback-notes">
                {originalMessage?.notes || '—'}
              </div>
            </div>
            <details className="border-top pt-2">
              <summary className="text-primary">
                {t('fn.moreDetails', 'More details')}
              </summary>
              <dl className="row mt-2 mb-0">
                <dt className="col-4">{t('lbl.sentBy', 'Sent by')}</dt>
                <dd className="col-8">{originalMessage?.username || '—'}</dd>
                <dt className="col-4">{t('lbl.received', 'Received')}</dt>
                <dd className="col-8">
                  {new Date(
                    originalMessage?.created_at || existing.created_at
                  ).toLocaleString()}
                </dd>
                <dt className="col-4">{t('lbl.colorCard', 'Color card')}</dt>
                <dd className="col-8">{originalMessage?.colourcard || '—'}</dd>
                <dt className="col-4">{t('lbl.priority', 'Priority')}</dt>
                <dd className="col-8">
                  {{
                    L: t('lbl.low', 'Low'),
                    N: t('lbl.normal', 'Normal'),
                    H: t('lbl.high', 'High'),
                  }[existing.priority] || existing.priority}
                </dd>
              </dl>
            </details>
          </div>
        ) : (
          <>
            {!request && (!validOrderId || lookupFailed) && (
              <Alert color="danger">
                {validOrderId
                  ? t(
                      'msg.formulaFeedbackLookupFailed',
                      'Could not check whether feedback was sent.'
                    )
                  : t(
                      'msg.formulaFeedbackOrderRequired',
                      'A saved order is required for feedback.'
                    )}
                {validOrderId && (
                  <Button color="link" onClick={() => onLookup(orderid)}>
                    {t('fn.retry', 'Retry')}
                  </Button>
                )}
              </Alert>
            )}
            {!request && validOrderId && !lookupReady && !lookupFailed && (
              <Alert color="info">
                <Spinner />
                {t('lbl.loading', 'Loading…')}
              </Alert>
            )}
            <div className="row">
              <FormGroup className="col-md-6">
                <Label>{t('lbl.product', 'Product')}</Label>
                <Input
                  value={form.productname || ''}
                  readOnly
                  invalid={Boolean(fieldErrors.productname)}
                />
              </FormGroup>
              <FormGroup className="col-md-6">
                <Label>{t('lbl.canSize', 'Can size')}</Label>
                <Input
                  value={form.cansizecode || ''}
                  onChange={update('cansizecode')}
                />
              </FormGroup>
              <FormGroup className="col-md-6">
                <Label>{t('lbl.colorCode', 'Color code')}</Label>
                <Input
                  value={form.colourcode || ''}
                  onChange={update('colourcode')}
                  invalid={Boolean(fieldErrors.colourcode)}
                />
              </FormGroup>
              <FormGroup className="col-md-6">
                <Label>{t('lbl.colorCard', 'Color card')}</Label>
                <Input
                  value={form.colourcard || ''}
                  onChange={update('colourcard')}
                />
              </FormGroup>
              <FormGroup className="col-md-6">
                <Label>{t('lbl.colorName', 'Color name')}</Label>
                <Input
                  value={form.colourname || ''}
                  onChange={update('colourname')}
                />
              </FormGroup>
              {!request && (
                <FormGroup className="col-md-6">
                  <Label>{t('lbl.priority', 'Priority')}</Label>
                  <Input
                    type="select"
                    value={form.priority || 'N'}
                    onChange={update('priority')}
                  >
                    <option value="L">{t('lbl.low', 'Low')}</option>
                    <option value="N">{t('lbl.normal', 'Normal')}</option>
                    <option value="H">{t('lbl.high', 'High')}</option>
                  </Input>
                </FormGroup>
              )}
            </div>
            {fieldErrors.formula && (
              <Alert color="danger">{fieldErrors.formula}</Alert>
            )}
            <FormGroup>
              <Label>{t('lbl.notes', 'Notes')}</Label>
              <Input
                type="textarea"
                rows="5"
                value={form.notes || ''}
                onChange={update('notes')}
                placeholder={
                  request
                    ? t(
                        'prompt.formulaRequestNotes',
                        'Add details of the formula request'
                      )
                    : t(
                        'prompt.formulaFeedbackNotes',
                        'Write details of the formula feedback'
                      )
                }
              />
            </FormGroup>

            {submitted && (
              <div className="text-center w-100">
                {supportState?.creating ? (
                  <Alert color="info">
                    <Spinner />
                    {t(
                      'msg.formulaSupportSending',
                      request
                        ? 'Sending formula request…'
                        : 'Sending formula feedback…'
                    )}
                  </Alert>
                ) : supportState?.error ? (
                  <Alert color="danger">
                    {t(
                      'msg.formulaSupportSendFailed',
                      'The formula support message could not be sent.'
                    )}
                  </Alert>
                ) : (
                  <Alert color="success">
                    {t(
                      request
                        ? 'msg.formulaSupportRequestSent'
                        : 'msg.formulaSupportFeedbackSent',
                      request
                        ? 'Formula request sent.'
                        : 'Formula feedback sent.'
                    )}
                  </Alert>
                )}
              </div>
            )}
          </>
        )}
      </ModalBody>

      <ModalFooter>
        {existing ? (
          <>
            {canOpenCase && (
              <Button
                color="primary"
                outline
                onClick={() => onOpenCase(existing.supportcaseid, orderid)}
              >
                {t(
                  'fn.openFormulaFeedback',
                  'Open feedback in Formula Support'
                )}
              </Button>
            )}
            <Button color="secondary" onClick={onCancel}>
              {t('fn.close', 'Close')}
            </Button>
          </>
        ) : submitted && !supportState?.error ? (
          <Button
            color="primary"
            onClick={onCancel}
            disabled={supportState?.creating}
          >
            {t('fn.ok', 'OK')}
          </Button>
        ) : (
          <>
            <Button color="secondary" onClick={onCancel}>
              {t('fn.cancel', 'Cancel')}
            </Button>
            <Button
              color="primary"
              disabled={
                (submitted && !supportState?.error) ||
                productInvalid ||
                colourInvalid ||
                formulaInvalid ||
                (!request && (!validOrderId || !lookupReady))
              }
              onClick={() => {
                setSubmitted(true);
                onSubmit({ ...form, rgb: nullableValue(form.rgb) });
              }}
            >
              {request
                ? t('fn.sendFormulaRequest', 'Send formula request')
                : t('fn.sendIssueReport', 'Send issue report')}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

FormulaSupportModal.propTypes = {
  type: PropTypes.oneOf(['request', 'feedback']).isRequired,
  show: PropTypes.bool.isRequired,
  order: PropTypes.object,
  requestProduct: PropTypes.object,
  requestColor: PropTypes.object,
  requestColorCode: PropTypes.string,
  currentUser: PropTypes.shape({ username: PropTypes.string }),
  supportState: PropTypes.shape({
    creating: PropTypes.bool,
    error: PropTypes.any,
    lookupOrderId: PropTypes.number,
    lookupLoading: PropTypes.bool,
    lookupError: PropTypes.bool,
    orderFeedbackCase: PropTypes.object,
  }),
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onLookup: PropTypes.func,
  onOpenCase: PropTypes.func,
  canOpenCase: PropTypes.bool,
  t: PropTypes.func.isRequired,
};
